import type { ParentPerformanceReportParams, ParentScore } from "@api/generated/model";
import { useParentPerformanceReport } from "@api/generated/parent-performance/parent-performance";
import { ONCE } from "@app/queryClient";
import { useTrial } from "@app/trials";
import { useSeasonFromLink } from "@app/workspace";
import { hint } from "@i18n";
import { Button, Loader, SegmentedControl, Select } from "@mantine/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatNumber } from "@ui/format";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

// the legacy's four reports: what they count, and the trials they start from (A → F4 has no target)
const TYPES = {
  f2_a: { kind: "rows", source: "F2", target: "A" },
  a_f4: { kind: "selected", source: "A", target: null },
  f4_b: { kind: "carried", source: "F4", target: "B" },
  b_b: { kind: "carried", source: "B", target: "B" },
} as const;
type Type = keyof typeof TYPES;

export const Route = createFileRoute("/genealogy/parent-performance")({
  validateSearch: z.object({ ev: z.number().int().optional(), type: z.enum(["f2_a", "a_f4", "f4_b", "b_b"]).optional() }),
  component: ParentPerformancePage,
});

/** Genealógia → Szülők teljesítménye (the legacy frmParentPerformance): each parent's lines from a source trial of the
 *  working season to the target (the next year's trial; A → F4 stays in the season). */
function ParentPerformancePage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const season = useSeasonFromLink(search.ev);
  const type: Type = search.type ?? "f2_a";
  return (
    <div className="page fill">
      <PageHeader title={t("parentPerformance.title")} subtitle={t("parentPerformance.subtitle")} season />
      <SegmentedControl
        value={type}
        onChange={(v) => void navigate({ search: (s) => ({ ...s, type: v as Type }) })}
        data={(Object.keys(TYPES) as Type[]).map((k) => ({ value: k, label: t(`parentPerformance.type.${k}`) }))}
        style={{ alignSelf: "flex-start" }}
        mb="sm"
      />
      {season != null && <Report key={`${type}-${season}`} type={type} season={season} />}
    </div>
  );
}

/** One report type of one season (a new type or season starts afresh). */
function Report({ type, season }: { type: Type; season: number }) {
  const { t } = useTranslation();
  const spec = TYPES[type];
  const [source, setSource] = useState<string | null>(spec.source);
  const [target, setTarget] = useState<string | null>(spec.target);
  const [asked, setAsked] = useState<ParentPerformanceReportParams | null>(null);
  const { trials } = useTrial(season, undefined, undefined);
  const { trials: next } = useTrial(spec.target ? season + 1 : undefined, undefined, undefined);
  const report = useParentPerformanceReport(asked ?? { kind: spec.kind, ev: season, source: "" }, { query: { enabled: !!asked, ...ONCE } });
  useEffect(() => {
    if (report.error) notifyError(report.error);
  }, [report.error]);
  // the type's usual trials are only proposed when this season has them
  const known = (kis: string | null, list: { kis: string }[]) => (kis && list.some((x) => x.kis === kis) ? kis : null);
  const src = known(source, trials);
  const tgt = known(target, next);
  const ready = !!src && (!spec.target || !!tgt);
  const data = asked ? report.data : undefined;
  const rows = spec.kind === "rows";
  const carried = spec.kind === "carried";
  const columns = useMemo<Column<ParentScore>[]>(() => {
    const figure = (id: keyof ParentScore & string, header: string, value: (s: ParentScore) => number | null, digits?: number) =>
      ({
        id,
        header,
        value,
        numeric: true,
        className: "mono",
        cell: digits == null ? undefined : (s) => formatNumber(value(s), digits),
      }) satisfies Column<ParentScore>;
    return [
      { id: "parent", header: t("parentPerformance.parent"), value: (s) => s.parent },
      figure("plots", carried ? t("parentPerformance.plots") : t("parentPerformance.occurrences"), (s) => s.plots),
      ...(carried ? [] : [figure("total", t("parentPerformance.rows"), (s) => s.total, 0)]),
      ...(rows
        ? [figure("mean", t("parentPerformance.mean"), (s) => s.mean, 1), figure("sd", t("parentPerformance.sd"), (s) => s.sd, 1)]
        : [
            figure("reached", carried ? t("parentPerformance.carried") : t("parentPerformance.selected"), (s) => s.reached, 0),
            figure("percent", "%", (s) => s.percent, 1),
          ]),
    ];
  }, [t, rows, carried]);
  const table = useDataTable("genealogy.parent-performance.parents", columns, data?.parents);
  return (
    <>
      <section className="card pad stack" style={{ gap: 10 }}>
        <p className="sm mu">{t(`parentPerformance.description.${spec.kind}`)}</p>
        <div className="hstack" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Select
            placeholder={hint("searchSelect")}
            size="xs"
            w={220}
            searchable
            label={t("parentPerformance.sourceTrial", { ev: season })}
            value={src}
            onChange={setSource}
            data={trials.map((x) => x.kis)}
          />
          {spec.target && (
            <Select
              placeholder={hint("searchSelect")}
              size="xs"
              w={220}
              searchable
              label={t("parentPerformance.targetTrial", { ev: season + 1 })}
              value={tgt}
              onChange={setTarget}
              data={next.map((x) => x.kis)}
              description={next.length ? undefined : t("parentPerformance.noTargetTrial", { ev: season + 1 })}
            />
          )}
          <Button
            disabled={!ready}
            loading={report.isFetching}
            onClick={() =>
              src && setAsked({ kind: spec.kind, ev: season, source: src, target: spec.target ? (tgt ?? undefined) : undefined })
            }
          >
            {t("parentPerformance.run")}
          </Button>
        </div>
      </section>
      <section className="card stack grow" style={{ gap: 0, minHeight: 0 }}>
        {!asked ? (
          <p className="sm mu inset">{t("parentPerformance.empty")}</p>
        ) : report.isFetching ? (
          <Loader size="sm" m="md" />
        ) : !data ? null : !data.parents.length ? (
          <p className="sm mu inset">{t("parentPerformance.noResults")}</p>
        ) : (
          <>
            <p className="sm mu inset">
              {t("parentPerformance.readCount", { count: data.plots, parent: data.parents.length })}
              {data.unmatched > 0 && ` · ${t("parentPerformance.withoutParent", { count: data.unmatched })}`}
            </p>
            <DataTable table={table} rowKey={(s) => s.parent} className="grow" />
          </>
        )}
      </section>
    </>
  );
}
